/* ===================================================================
   Session provider (Hopper, 2026-06-09).

   Wraps the app with the current Supabase Auth session and keeps it in
   sync via supabase.auth.onAuthStateChange. Components read it with the
   useSession() hook (src/auth/useSession.ts). The context object + types
   live in src/auth/context.ts.

   This provider is mounted in src/main.tsx (NOT App.tsx -- Charlie owns
   App.tsx routing). It exposes session / user / loading / configured /
   signOut().

   It deliberately does NOT do any routing/redirects -- that stays with
   Auth.tsx (post-login) and Charlie's route guards.
   =================================================================== */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import {
  supabase,
  supabaseConfigured,
  isRecoveryFlagged,
  clearRecoveryFlag,
} from '../lib/supabase';
import { SessionContext } from './context';
import type { SessionContextValue } from './context';

/* Detect a password-recovery callback SYNCHRONOUSLY on first load.
   isRecoveryFlagged() reads a durable sessionStorage flag that lib/supabase.ts
   captures at MODULE LOAD -- before createClient()'s detectSessionInUrl can
   strip the `type=recovery` fragment -- and falls back to the live URL hash.
   This is robust to: the async hash strip winning the race, an existing/stale
   session being present, and SessionProvider re-mounting. The async
   PASSWORD_RECOVERY auth event (below) is a third, redundant trigger. */
function hashHasRecovery(): boolean {
  return isRecoveryFlagged();
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  // If the anon key isn't set yet there is no session to fetch, so start
  // un-loaded in that case (avoids a synchronous setState in the effect).
  const [loading, setLoading] = useState(supabaseConfigured);
  // Seed from the URL hash so the very first paint already knows this is a
  // recovery flow, before any async auth event fires.
  const [passwordRecovery, setPasswordRecovery] = useState<boolean>(hashHasRecovery);

  useEffect(() => {
    if (!supabaseConfigured) return;
    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (active) {
          setSession(data.session);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (!active) return;
      setSession(next);
      // The recovery link logs the user in with a recovery session and emits
      // PASSWORD_RECOVERY. Trap it: the app must force a set-password step
      // before routing in. A real SIGNED_OUT clears the flag.
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      else if (event === 'SIGNED_OUT') setPasswordRecovery(false);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      configured: supabaseConfigured,
      passwordRecovery,
      clearPasswordRecovery: () => {
        clearRecoveryFlag();
        setPasswordRecovery(false);
      },
      signOut: async () => {
        await supabase.auth.signOut();
      },
    }),
    [session, loading, passwordRecovery],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
